import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/lib/api';

interface H5pLibrary {
  id: string;
  name: string;
  machineName: string;
  versionString: string;
  runnable: boolean;
  packagesCount: number;
  dependentsCount: number;
  isOutdated: boolean;
}

interface LibrariesResponse {
  data: H5pLibrary[];
  outdatedCount?: number;
}

export function H5pLibrariesPage() {
  const [search, setSearch] = useState('');
  const applied = useDebouncedValue(search, 300).trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['h5p', 'libraries', applied],
    queryFn: () => {
      const qs = applied ? `?search=${encodeURIComponent(applied)}` : '';
      return api<LibrariesResponse>(`/h5p/libraries${qs}`);
    },
  });

  const libraries = data?.data ?? [];
  const outdatedCount = data?.outdatedCount ?? 0;

  return (
    <div className="lms-h5p-libraries lms-site-admin-hub-page font-sans">
      <header className="flex items-start gap-4">
        <HubBackLink to="/h5p" label="Volver a Biblioteca H5P" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Librerías H5P instaladas</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Catálogo de librerías registradas automáticamente al importar paquetes, con sus versiones y
            compatibilidad.
          </p>
        </div>
      </header>

      {outdatedCount > 0 ? (
        <section className="lms-h5p-libraries__alert" role="status">
          <span className="material-symbols-outlined lms-h5p-libraries__alert-icon" aria-hidden="true">
            info
          </span>
          <p>
            <strong>{outdatedCount} librería(s)</strong> tienen una versión más reciente registrada en el
            sistema. Revisa las actividades que las usan antes de publicar nuevas versiones.
          </p>
        </section>
      ) : null}

      <section className="lms-h5p-libraries__toolbar">
        <form method="get" action="/h5p/libraries" onSubmit={(event) => event.preventDefault()}>
          <SearchInput
            name="search"
            value={search}
            onChange={setSearch}
            placeholder="Buscar..."
            aria-label="Buscar librería"
            className="lms-h5p-libraries__search"
          />
        </form>
      </section>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <section className="lms-h5p-libraries__table-card">
          <div className="lms-h5p-libraries__table-scroll">
            <table className="lms-h5p-libraries__table">
              <thead>
                <tr>
                  <th>Librería</th>
                  <th>Versión</th>
                  <th>Ejecutable</th>
                  <th className="lms-h5p-libraries__th--center">Actividades</th>
                  <th className="lms-h5p-libraries__th--center">Dependiente de</th>
                  <th className="lms-h5p-libraries__th--end">Compatibilidad</th>
                </tr>
              </thead>
              <tbody>
                {libraries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="lms-h5p-libraries__empty">
                      No se encontraron librerías.
                    </td>
                  </tr>
                ) : (
                  libraries.map((lib) => (
                    <tr
                      key={lib.id}
                      className={lib.isOutdated ? 'lms-h5p-libraries__row--outdated' : undefined}
                    >
                      <td>
                        <Link to={`/h5p/libraries/${lib.id}`} className="lms-h5p-libraries__name">
                          {lib.machineName}
                        </Link>
                      </td>
                      <td className="lms-h5p-libraries__muted">{lib.versionString}</td>
                      <td className="lms-h5p-libraries__muted">{lib.runnable ? 'Sí' : 'No'}</td>
                      <td className="lms-h5p-libraries__center lms-h5p-libraries__muted">
                        {lib.packagesCount}
                      </td>
                      <td className="lms-h5p-libraries__center lms-h5p-libraries__muted">
                        {lib.dependentsCount}
                      </td>
                      <td className="lms-h5p-libraries__end">
                        {lib.isOutdated ? (
                          <span className="lms-h5p-libraries__badge lms-h5p-libraries__badge--outdated">
                            <span className="material-symbols-outlined" aria-hidden="true">
                              warning
                            </span>
                            Desactualizada
                          </span>
                        ) : (
                          <span className="lms-h5p-libraries__badge lms-h5p-libraries__badge--ok">
                            <span className="material-symbols-outlined" aria-hidden="true">
                              check_circle
                            </span>
                            Al día
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
